import React, { useMemo, useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Checkbox,
  Chip,
  Alert,
  CircularProgress,
  Tooltip,
  useTheme,
} from "@mui/material";
import PropTypes from "prop-types";
import { createLoopKPIAction, updateLoopKPIAction } from "../../../../store/actions/loopActions";
import KPITypeChip from "../../../CustomComponents/KPITypeChip";
import MultiSelectFilter from "../../../CustomComponents/InputFields/MultiSelectFilter";


export default function LoopKPISelector({ loopId, editable = true }) {
  const dispatch = useDispatch();
  const muiTheme = useTheme();
  const { loopKPIs, loading, loopThemes } = useSelector((state) => state.loop);
  const { flatKpis, flatThemes } = useSelector((state) => state.themes);
  const [selectedThemes, setSelectedThemes] = useState(
    loopThemes
      .filter((lt) => lt.importance > 0)
      .map((lt) => (
        { value: lt.theme, label: flatThemes[lt.theme].name }
      ))
  );

  const allKPIs = Object.values(flatKpis);

  // Memoize selected KPIs to prevent unnecessary re-renders
  const selectedKPIs = useMemo(() => {
    const selected = new Set();
    loopKPIs.forEach((loopKPI) => {
      if (loopKPI.is_selected) {
        selected.add(loopKPI.kpi);
      }
    });
    return selected;
  }, [loopKPIs]);

  const selectedKPINames = useMemo(() => {
    const selected = new Set();
    loopKPIs.forEach((loopKPI) => {
      if (loopKPI.is_selected) {
        selected.add(flatKpis[loopKPI.kpi].name);
      }
    });
    return selected;
  }, [loopKPIs]);

  // No fetching here; data comes embedded with the project payload

  const handleKPIChange = useCallback(
    (kpiId, checked) => {
      if (!editable) return;

      try {
        const existingLoopKPI = loopKPIs.find((lk) => lk.kpi === kpiId);

        if (checked) {
          // Add KPI to loop
          if (!existingLoopKPI) {
            dispatch(
              createLoopKPIAction({
                loop: loopId,
                kpi: kpiId,
                is_selected: true,
              })
            );
          } else if (!existingLoopKPI.is_selected) {
            // Update existing KPI to be selected
            dispatch(
              updateLoopKPIAction(existingLoopKPI.id, {
                ...existingLoopKPI,
                is_selected: true,
              })
            );
          }
        } else if (existingLoopKPI?.is_selected) {
          // Mark as not selected instead of deleting
          dispatch(
            updateLoopKPIAction(existingLoopKPI.id, {
              ...existingLoopKPI,
              is_selected: false,
            })
          );
        }
      } catch (error) {
        console.error("Error updating KPI selection:", error);
      }
    },
    [editable, loopKPIs, loopId, dispatch]
  );

  const getKPISuperThemeColor = (supertheme) => {
    const superThemeColors = {
      0: muiTheme.palette.primary.light,
      1: muiTheme.palette.primary.dark,
      2: muiTheme.palette.secondary.main,
    };
    const contrastColors = {
      0: muiTheme.palette.primary.contrastText,
      1: muiTheme.palette.text.white,
      2: muiTheme.palette.secondary.contrastText,
    };

    return { backgroundColor: superThemeColors[supertheme], color: contrastColors[supertheme] };
  };


  const groupedKPIs = useMemo(() => {
    const selectedThemeIds = selectedThemes.map(theme => theme.value);
    const groups = {};

    // Group KPIs by theme
    (allKPIs || []).forEach((kpi) => {
      const themeInfo = flatThemes[kpi.theme];
      if (themeInfo) {
        // Filter based on selected themes
        if (selectedThemes.length > 0 && !selectedThemeIds.includes(kpi.theme)) {
          return;
        }
        
        const themeKey = `${themeInfo.supertheme}-${themeInfo.name}`;
        if (!groups[themeKey]) {
          groups[themeKey] = {
            theme: themeInfo,
            kpis: [],
          };
        }
        groups[themeKey].kpis.push(kpi);
      }
    });

    // Sort groups by supertheme, then by theme name
    const sortedGroups = Object.values(groups).sort((a, b) => {
      const superthemeComparison = a.theme.supertheme - b.theme.supertheme;
      if (superthemeComparison !== 0) {
        return superthemeComparison;
      }
      return a.theme.name.localeCompare(b.theme.name);
    });

    // Sort KPIs within each group
    sortedGroups.forEach((group) => {
      group.kpis.sort((a, b) => a.name.localeCompare(b.name));
    });

    return sortedGroups;
  }, [allKPIs, flatThemes, selectedThemes]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  const conditionalAlert = (display, message, severity, children) => {
    if (display) {
      return (
        <Alert severity={severity}>
          {message}
          {children}
        </Alert>
      );
    }
    return children;
  };

  return (
    <Box>
      <Typography variant="h6" component="h2" gutterBottom>
        KPI's
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Selecteer de KPI's die je wilt gebruiken voor deze ontwerpronde. 
        Je kunt KPI's aan- en uitvinken om ze te activeren of deactiveren.
      </Typography>

      <Box>
        <MultiSelectFilter
          label="Filter op aspecten"
          options={Object.values(flatThemes).map((theme) => ({
            value: theme.id,
            label: theme.name,
          }))}
          values={selectedThemes}
          setValue={setSelectedThemes}
          placeholder="Alle aspecten"
        />
      </Box>

      <Box p={2} bgcolor="success.50" borderRadius={1}>
        <Typography variant="body2" color="success.main">
                    {selectedKPIs.size} KPI{selectedKPIs.size !== 1 ? "'s" : ""} geselecteerd voor deze ronde
        </Typography>
      </Box>

      {groupedKPIs.length === 0 ? (
        <Alert severity="info">Geen KPI's beschikbaar. Voeg eerst KPI's toe aan het systeem.</Alert>
      ) : (
        <Box>
          {groupedKPIs.map((group) => (
            <Box key={`${group.theme.supertheme}-${group.theme.name}`} sx={{ mb: 3 }}>
              {/* Theme Header */}
              <Box sx={{ mb: 1 }}>
                <Tooltip title={group.theme.description || "Geen beschrijving beschikbaar"} placement="top" arrow>
                  <Typography
                    variant="h6"
                    component="h3"
                    sx={{
                      fontWeight: "bold",
                      color: "text.primary",
                      cursor: "help",
                      borderBottom: 2,
                      borderColor: "primary.main",
                      pb: 0.5,
                      display: "inline-block",
                    }}
                  >
                    {group.theme.name}
                  </Typography>
                </Tooltip>
              </Box>

              {/* KPIs under this theme */}
              <List sx={{ width: "100%", bgcolor: "background.paper", borderRadius: 1 }}>
                {group.kpis.map((kpi) => {
                  const isSelected = selectedKPIs.has(kpi.id);
                  const isSelectedInOtherTheme = !isSelected && selectedKPINames.has(kpi.name);
                  return (
                    <ListItem key={kpi.id} disablePadding>
                      {conditionalAlert(
                        !isSelected && isSelectedInOtherTheme,
                        `Deze KPI (${kpi.name}) is al geselecteerd binnen een ander thema en is niet beschikbaar voor dit thema`,
                        "warning",
                        <ListItemButton
                          dense
                          aria-disabled={!editable}
                          tabIndex={editable ? 0 : -1}
                          disableRipple={!editable || isSelectedInOtherTheme}
                          disableTouchRipple={!editable || isSelectedInOtherTheme}
                          onClick={editable && !isSelectedInOtherTheme ? () => handleKPIChange(kpi.id, !isSelected) : null}
                          sx={{
                            ...(editable && !isSelectedInOtherTheme
                              ? {
                                  "&:hover": {
                                    bgcolor: isSelected ? "primary.50" : "grey.50",
                                  },
                                  cursor: "pointer",
                                }
                              : {
                                  "&:hover": { bgcolor: "transparent" },
                                  cursor: "default",
                                }),
                            bgcolor: isSelected ? "primary.25" : "transparent",
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: 40 }}>
                            <Checkbox checked={isSelected} disabled={!editable || isSelectedInOtherTheme} color="primary" />
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Box display="flex" alignItems="center" gap={1}>
                                <Typography
                                  variant="body1"
                                  fontWeight={isSelected ? "medium" : "normal"}
                                  color={isSelected ? "primary.main" : "text.primary"}
                                >
                                  {kpi.name} {kpi.name === "Score" && ` ${group.theme.name}`}
                                </Typography>
                                {kpi.unit && <Chip label={kpi.unit} size="small" variant="outlined" />}
                                <KPITypeChip scaleType={kpi.scale_type} />
                              </Box>
                            }
                            secondary={
                              <Box>
                                <Typography variant="body2" color="text.secondary">
                                  {kpi.description || "Geen beschrijving"}
                                </Typography>
                              </Box>
                            }
                          />
                        </ListItemButton>
                      )}
                    </ListItem>
                  );
                })}
              </List>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}

LoopKPISelector.propTypes = {
  loopId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  editable: PropTypes.bool,
};
